import {
  type PlantKPIs,
  type ActualAndPlannedData,
  KpisList,
  type AlternativeFuelRates
} from '@domain/kpis'
import {HierarchyNodeType} from '@domain/shared'
import {DateRange} from '@hconnect/uikit'
import {capitalize} from 'lodash'
import moment from 'moment-timezone'

import {
  MeanTimeBetweenFailures,
  KilnCoefficients,
  KilnHeatConsumptions,
  TechnicalCementPowerConsumption,
  KpiDataGeneratorData,
  KpiDataGeneratorError,
  KpiQuarterlyData,
  QuickSelectionId
} from '../types'

export const tabIndexFiltered = [KpisList.ClinkerProduction, KpisList.CementProduction]

export const TabIndexDataMap: Record<
  KpisList,
  {
    id: KpisList
    planned: string
    actual: string
    delta: string
    plannedYearly: string
    quarterlyForecast: string
  }
> = {
  [KpisList.ClinkerProduction]: {
    id: KpisList.ClinkerProduction,
    planned: 'plannedClinkerProduction',
    actual: 'actualClinkerProduction',
    delta: 'clinkerDelta',
    plannedYearly: 'plannedYearlyClinkerProductionVolume',
    quarterlyForecast: 'quarterlyForecastClinkerProduction'
  },
  [KpisList.CementProduction]: {
    id: KpisList.CementProduction,
    planned: 'plannedCementProduction',
    actual: 'actualCementProduction',
    delta: 'cementDelta',
    plannedYearly: 'plannedYearlyCementProductionVolume',
    quarterlyForecast: 'quarterlyForecastCementProduction'
  },
  [KpisList.KilnReliabilityCoefficient]: {
    id: KpisList.KilnReliabilityCoefficient,
    planned: 'plannedReliabilityCoefficient',
    actual: 'actualReliabilityCoefficient',
    delta: 'reliabilityDelta',
    plannedYearly: 'plannedYearlyReliabilityCoefficient',
    quarterlyForecast: 'quarterlyForecastReliabilityCoefficient'
  },
  [KpisList.KilnOperatingCoefficient]: {
    id: KpisList.KilnOperatingCoefficient,
    planned: 'plannedOperatingCoefficient',
    actual: 'actualOperatingCoefficient',
    delta: 'operatingDelta',
    plannedYearly: 'plannedYearlyOperatingCoefficient',
    quarterlyForecast: 'quarterlyForecastOperatingCoefficient'
  },
  [KpisList.KilnHeatConsumption]: {
    id: KpisList.KilnHeatConsumption,
    planned: 'plannedKilnHeatConsumptions',
    actual: 'actualKilnHeatConsumptions',
    delta: 'delta',
    plannedYearly: 'plannedYearlyKilnHeatConsumptions',
    quarterlyForecast: 'quarterlyForecastKilnHeatConsumptions'
  },
  [KpisList.AlternativeFuelRate]: {
    id: KpisList.AlternativeFuelRate,
    planned: 'plannedAlternativeFuelRates',
    actual: 'actualAlternativeFuelRates',
    delta: 'delta',
    plannedYearly: 'plannedYearlyProductionVolume',
    quarterlyForecast: 'quarterlyForecastAlternativeFuelRates'
  },
  [KpisList.MTBF]: {
    id: KpisList.MTBF,
    planned: 'plannedMeanTimeBetweenFailures',
    actual: 'actualMeanTimeBetweenFailures',
    delta: 'delta',
    plannedYearly: 'plannedYearlyProductionVolume',
    quarterlyForecast: 'quarterlyForecastMeanTimeBetweenFailures'
  },
  [KpisList.TechnicalCementPowerCons]: {
    id: KpisList.TechnicalCementPowerCons,
    planned: 'plannedTechnicalCementPowerConsumptions',
    actual: 'actualTechnicalCementPowerConsumptions',
    delta: 'delta',
    plannedYearly: 'plannedYearlyTechnicalCementPowerConsumptions',
    quarterlyForecast: 'quarterlyForecastTechnicalCementPowerConsumptions'
  }
}

export const getComparisonDate = (
  {from, to}: DateRange<Date>,
  locale: string,
  offsetYear?: number
) => {
  const year = offsetYear
    ? moment(to).subtract(offsetYear, 'year').format("'YY")
    : moment(to).format("'YY")

  const fromMonth = moment(from).locale(locale).format('MMM')
  const toMonth = moment(to).locale(locale).format('MMM')
  const month =
    fromMonth === toMonth
      ? capitalize(toMonth)
      : `${capitalize(fromMonth)} - ${capitalize(toMonth)}`

  return `${month} ${year}`
}

export const getQuarterlyLabel = (month: number) => {
  const quarterLabelMapping = {
    0: null,
    1: 'FA',
    2: 'FJ',
    3: 'FO'
  }
  const quarter = Math.floor(month / 3)
  return quarterLabelMapping[quarter]
}

const generatePlantMeanTimeBetweenFailuresData = (
  plantId: string,
  meanTimeBetweenFailuresData?: MeanTimeBetweenFailures
): {meanTimeBetweenFailure: ActualAndPlannedData} | undefined => {
  const plantMeanTimeBetweenFailures = meanTimeBetweenFailuresData?.descendants?.find(
    (item) => item.plantId === plantId
  )
  return (
    plantMeanTimeBetweenFailures && {
      meanTimeBetweenFailure: {
        unit: plantMeanTimeBetweenFailures.unit,
        planned: plantMeanTimeBetweenFailures.plannedMeanTimeBetweenFailures,
        actual: plantMeanTimeBetweenFailures.actualMeanTimeBetweenFailures,
        delta: plantMeanTimeBetweenFailures.delta
      }
    }
  )
}

const generatePlantAlternativeFuelRates = (
  plantId: string,
  alternativeFuelRatesData?: AlternativeFuelRates
): {alternativeFuelRate: ActualAndPlannedData} | undefined => {
  const plantAlternativeFuel = alternativeFuelRatesData?.descendants?.find(
    (item) => item.plantId === plantId
  )
  return (
    plantAlternativeFuel && {
      alternativeFuelRate: {
        unit: plantAlternativeFuel.unit,
        planned: plantAlternativeFuel.plannedAlternativeFuelRates,
        actual: plantAlternativeFuel.actualAlternativeFuelRates,
        delta: plantAlternativeFuel.delta
      }
    }
  )
}

const generatePlantKilnCoefficient = (
  plantId: string,
  kilnCoefficientData?: KilnCoefficients
):
  | {
      kilnReliabilityCoefficient: ActualAndPlannedData
      kilnOperatingCoefficient: ActualAndPlannedData
    }
  | undefined => {
  const plantCoefficientData = kilnCoefficientData?.descendants?.find(
    (item) => item.plantId === plantId
  )
  return (
    plantCoefficientData && {
      kilnReliabilityCoefficient: {
        unit: plantCoefficientData.unit,
        planned: plantCoefficientData.plannedReliabilityCoefficient,
        actual: plantCoefficientData.actualReliabilityCoefficient,
        delta: plantCoefficientData.reliabilityDelta
      },
      kilnOperatingCoefficient: {
        unit: plantCoefficientData.unit,
        planned: plantCoefficientData.plannedOperatingCoefficient,
        actual: plantCoefficientData.actualOperatingCoefficient,
        delta: plantCoefficientData.operatingDelta
      }
    }
  )
}

const generatePlantKilnHeatConsumptions = (
  plantId: string,
  kilnHeatConsumptionsData?: KilnHeatConsumptions
): {kilnHeatConsumption: ActualAndPlannedData} | undefined => {
  const plantKilnHeatConsumption = kilnHeatConsumptionsData?.descendants?.find(
    (item) => item.plantId === plantId
  )
  return (
    plantKilnHeatConsumption && {
      kilnHeatConsumption: {
        unit: plantKilnHeatConsumption.unit,
        planned: plantKilnHeatConsumption.plannedKilnHeatConsumptions,
        actual: plantKilnHeatConsumption.actualKilnHeatConsumptions,
        delta: plantKilnHeatConsumption.delta
      }
    }
  )
}

const generatePlantTechnicalCementPowerConsumption = (
  plantId: string,
  technicalCementPowerConsumptionData?: TechnicalCementPowerConsumption
): {technicalCementPowerCons: ActualAndPlannedData} | undefined => {
  const plantTechnicalCementPowerCons = technicalCementPowerConsumptionData?.descendants?.find(
    (item) => item.plantId === plantId
  )
  return (
    plantTechnicalCementPowerCons && {
      technicalCementPowerCons: {
        unit: plantTechnicalCementPowerCons.unit,
        planned: plantTechnicalCementPowerCons.plannedTechnicalCementPowerConsumptions,
        actual: plantTechnicalCementPowerCons.actualTechnicalCementPowerConsumptions,
        delta: plantTechnicalCementPowerCons.delta
      }
    }
  )
}

export const generateKpiData = (
  plantId: string,
  {
    alternativeFuelRatesError,
    meanTimeBetweenFailuresError,
    kilnCoefficientError,
    kilnHeatConsumptionsError,
    technicalCementPowerConsumptionError
  }: KpiDataGeneratorError,
  {
    alternativeFuelRatesData,
    meanTimeBetweenFailuresData,
    kilnCoefficientData,
    kilnHeatConsumptionsData,
    technicalCementPowerConsumptionData
  }: KpiDataGeneratorData
): PlantKPIs => {
  const plantAlternativeFuelRates = !alternativeFuelRatesError
    ? generatePlantAlternativeFuelRates(plantId, alternativeFuelRatesData)
    : undefined
  const plantMeanTimeBetweenFailures = !meanTimeBetweenFailuresError
    ? generatePlantMeanTimeBetweenFailuresData(plantId, meanTimeBetweenFailuresData)
    : undefined
  const plantKilnCoefficient = !kilnCoefficientError
    ? generatePlantKilnCoefficient(plantId, kilnCoefficientData)
    : undefined
  const plantKilnHeatConsumptions = !kilnHeatConsumptionsError
    ? generatePlantKilnHeatConsumptions(plantId, kilnHeatConsumptionsData)
    : undefined
  const plantTechnicalCementPowerConsumption = !technicalCementPowerConsumptionError
    ? generatePlantTechnicalCementPowerConsumption(plantId, technicalCementPowerConsumptionData)
    : undefined

  return {
    alternativeFuelRate: {
      data: plantAlternativeFuelRates && {...plantAlternativeFuelRates.alternativeFuelRate},
      isError: alternativeFuelRatesError
    },
    meanTimeBetweenFailure: {
      data: plantMeanTimeBetweenFailures && {
        ...plantMeanTimeBetweenFailures.meanTimeBetweenFailure
      },
      isError: meanTimeBetweenFailuresError
    },
    kilnReliabilityCoefficient: {
      data: plantKilnCoefficient && {...plantKilnCoefficient.kilnReliabilityCoefficient},
      isError: kilnCoefficientError
    },
    kilnOperatingCoefficient: {
      data: plantKilnCoefficient && {...plantKilnCoefficient.kilnOperatingCoefficient},
      isError: kilnCoefficientError
    },
    technicalCementPowerConsumption: {
      data: plantTechnicalCementPowerConsumption && {
        ...plantTechnicalCementPowerConsumption.technicalCementPowerCons
      },
      isError: technicalCementPowerConsumptionError
    },
    kilnHeatConsumption: {
      data: plantKilnHeatConsumptions && {...plantKilnHeatConsumptions.kilnHeatConsumption},
      isError: kilnHeatConsumptionsError
    }
  }
}

export const checkIfQuarterlyDataExists = (
  currentTab: KpisList,
  kpiQuarterlyData: KpiQuarterlyData,
  locationId: string = HierarchyNodeType.Global
): boolean => {
  if (kpiQuarterlyData.locationId === locationId) {
    let result = false
    for (const descendant of kpiQuarterlyData.descendants) {
      const quarterlyForecast = descendant?.[TabIndexDataMap[currentTab].quarterlyForecast]
      result = result || !!quarterlyForecast
    }
    return result
  }

  let finalResult = false

  kpiQuarterlyData.descendants?.forEach((descendant: KpiQuarterlyData) => {
    finalResult = finalResult || checkIfQuarterlyDataExists(currentTab, descendant, locationId)
  })
  return finalResult
}

export const showYTMQuickSelectionIds: QuickSelectionId[] = [
  'yearToMonthYTM',
  'lastYear',
  'yearToDateYTD'
]

export const showYtmData = (quickSelectionId?: string | null): boolean => {
  return (
    !!quickSelectionId && showYTMQuickSelectionIds.includes(quickSelectionId as QuickSelectionId)
  )
}
